import type { ElasticListVehicleOdometerReading } from '../app/api/search/vehicles';
import type { Locale, Market } from '../config/domain-locale-map';

export const getAssignmentOdometerUnit = (market: Market): 'km' | 'mil' => {
  switch (market) {
    case 'drive':
      return 'km';
    default:
      return 'mil';
  }
};

interface MileageToOdometerOptions {
  market: Market;
  locale?: Locale;
}

export enum OdometerReadingUnit {
  ScandinavianMile = 'ScandinavianMile',
  Kilometer = 'Kilometer',
}

export const odometerReadingUnitByMarket = {
  wayke: OdometerReadingUnit.ScandinavianMile,
  drive: OdometerReadingUnit.Kilometer,
};

type OdometerUnit = 'Kilometer' | 'ScandinavianMile';

interface OdometerReading {
  unit: OdometerUnit;
  value: number;
}

export interface ConvertedOdometerReading {
  unit: OdometerReadingUnit;
  value: number;
}

const isScandinavianMile = (unit: OdometerUnit): boolean => unit === 'ScandinavianMile';

export const convertOdometerForMarket = <T extends OdometerReading>(
  odometer: T,
  market: Market,
): ConvertedOdometerReading => {
  const targetUnit = odometerReadingUnitByMarket[market];
  const isSourceScandinavianMile = isScandinavianMile(odometer.unit);
  const isTargetScandinavianMile = targetUnit === OdometerReadingUnit.ScandinavianMile;

  if (isSourceScandinavianMile === isTargetScandinavianMile) {
    return {
      unit: targetUnit,
      value: odometer.value,
    };
  }

  if (odometer.unit === OdometerReadingUnit.Kilometer && isTargetScandinavianMile) {
    return {
      unit: OdometerReadingUnit.ScandinavianMile,
      value: Math.round(odometer.value / 10),
    };
  }

  if (isSourceScandinavianMile && targetUnit === OdometerReadingUnit.Kilometer) {
    return {
      unit: OdometerReadingUnit.Kilometer,
      value: odometer.value * 10,
    };
  }

  return {
    unit: targetUnit,
    value: odometer.value,
  };
};

export const mileageToOdometer = (
  mileage: number,
  options: MileageToOdometerOptions,
): ElasticListVehicleOdometerReading => {
  if (options.locale) {
    switch (options.locale) {
      case 'sv':
        return {
          source: '',
          unit: OdometerReadingUnit.Kilometer,
          updatedAt: '',
          value: mileage,
        };
      case 'nb':
        return {
          source: '',
          unit: OdometerReadingUnit.ScandinavianMile,
          updatedAt: '',
          value: mileage * 10,
        };
      default:
        throw new Error(`Unsupported locale: ${options.locale}`);
    }
  }

  switch (options.market) {
    case 'wayke':
      return {
        source: '',
        unit: OdometerReadingUnit.Kilometer,
        updatedAt: '',
        value: mileage,
      };
    default:
      return {
        source: '',
        unit: OdometerReadingUnit.ScandinavianMile,
        updatedAt: '',
        value: mileage * 10,
      };
  }
};
